import React, { useCallback } from 'react';
import { cx } from '@linaria/atomic';
import { IconName } from '@frontend/icons';
import { contextFactory } from '../../hooks/context-factory';
import { Button } from '../button/button.component';
import { useFormField } from '../forms/hooks/use-form-field/use-form-field';
import { RadioFieldLayout } from '../forms/layouts/radio-field-layout.component';
import { useRadioContext } from '../forms/providers/radio-provider';
import { Text } from '../text/text.component';
import { containerStyle, optionContainerBaseStyle, optionContainerCheckedStyle } from './content-switcher.styles';

type ContentSwitcherCtx = (nextValue: string) => void;

const [ContentSwitcherCtx, useContentSwitcher] = contextFactory<ContentSwitcherCtx>(
  'ContentSwitcher must wrap its Options'
);

type Props<T extends string> = {
  children: Array<React.ReactElement | null>;
  defaultValue: T;
  onValueChange: (nextValue: T) => void;
};

export const ContentSwitcher = <T extends string>({ children, defaultValue, onValueChange }: Props<T>) => {
  const contentSwitcherFieldProps = useFormField({
    type: 'optionswitch',
    value: defaultValue,
  });

  const handleValueChange = useCallback(
    (nextValue: string) => {
      onValueChange(nextValue as T);
    },
    [onValueChange]
  );

  return (
    <ContentSwitcherCtx.Provider value={handleValueChange}>
      <RadioFieldLayout {...contentSwitcherFieldProps} name='contentSwitcher'>
        <div className={containerStyle}>{children}</div>
      </RadioFieldLayout>
    </ContentSwitcherCtx.Provider>
  );
};

type ContentSwitchOptionProps<T extends string> = {
  value: T;
  iconName?: IconName;
  label?: string;
  disabled?: boolean;
  trackingId?: string;
  children?: React.ReactNode;
};

export const ContentSwitchOption = <T extends string>({
  value,
  iconName,
  label,
  trackingId,
  children,
}: ContentSwitchOptionProps<T>) => {
  const { getOptionProps } = useRadioContext();
  const onValueChange = useContentSwitcher();

  const { checked, name, onChange, className: optClassName, ...optProps } = getOptionProps(value);

  const onClick = useCallback(() => {
    onChange({ name, value });
    onValueChange(value);
  }, [name, onChange, onValueChange, value]);

  return (
    <Button
      className={cx(optionContainerBaseStyle, checked && optionContainerCheckedStyle, optClassName)}
      {...optProps}
      iconName={iconName}
      onClick={onClick}
      type='button'
      trackingId={trackingId}
    >
      {children ?? (
        <Text className='content-switcher-label' size='medium'>
          {label}
        </Text>
      )}
    </Button>
  );
};

ContentSwitcher.Option = ContentSwitchOption;
